import { create } from "zustand";
import ApiService from "Helpers/ApiService";
import type GerminationTrayModel from "Models/GerminationTray";

interface GerminationTrayStoreState {
    germinationTrays: GerminationTrayModel[];
    currentGerminationTray?: GerminationTrayModel;
    getGerminationTray: (id: string) => Promise<GerminationTrayModel>;
    getGerminationTrays: () => Promise<GerminationTrayModel[]>;
    getTraysByName: (name: string) => Promise<GerminationTrayModel[]>;
    createGerminationTray: (tray: Omit<GerminationTrayModel, "_id">) => Promise<GerminationTrayModel>;
    savePlantingIds: (tray: GerminationTrayModel) => Promise<number>;
    deleteGerminationTray: (id: string) => Promise<void>;
    updatePlantingAtIndex: (index: number, plantingId?: string) => Promise<void>;
}

export const useGerminationTrayStore = create<GerminationTrayStoreState>((set, get) => ({
    germinationTrays: [],
    currentGerminationTray: undefined,
    getGerminationTray: async (id) => {
        const tray = await ApiService.getGerminationTray(id);
        if (!tray || typeof tray !== "object") {
            throw new Error("Unable to load germination tray");
        }
        set((state) => ({
            currentGerminationTray: tray,
            germinationTrays: state.germinationTrays.map((item) => item._id === tray._id ? tray : item),
        }));
        return tray;
    },
    getGerminationTrays: async () => {
        const trays = await ApiService.getGerminationTrays();
        if (!Array.isArray(trays)) {
            throw new Error("Unable to load germination trays");
        }
        set({ germinationTrays: trays });
        return trays;
    },
    getTraysByName: async (name) => {
        const trays = await ApiService.getTraysByName(name);
        if (!Array.isArray(trays)) {
            throw new Error("Unable to load germination trays");
        }
        set({ germinationTrays: trays });
        return trays;
    },
    createGerminationTray: async (tray) => {
        const createdTray = await ApiService.createGerminationTray(tray);
        if (!createdTray || typeof createdTray !== "object") {
            throw new Error("Unable to create germination tray");
        }
        set((state) => ({
            germinationTrays: [...state.germinationTrays.filter((item) => item._id !== createdTray._id), createdTray],
        }));
        return createdTray;
    },
    savePlantingIds: async (tray) => {
        const result = await ApiService.savePlantingIds(tray);
        set((state) => ({
            currentGerminationTray: state.currentGerminationTray?._id === tray._id ? tray : state.currentGerminationTray,
            germinationTrays: state.germinationTrays.map((item) => item._id === tray._id ? tray : item),
        }));
        return result;
    },
    deleteGerminationTray: async (id) => {
        await ApiService.deleteGerminationTray(id);
        set((state) => ({
            germinationTrays: state.germinationTrays.filter((tray) => tray._id !== id),
            currentGerminationTray: state.currentGerminationTray?._id === id ? undefined : state.currentGerminationTray,
        }));
    },
    updatePlantingAtIndex: async (index, plantingId) => {
        const tray = get().currentGerminationTray;
        if (!tray || index < 0 || index >= tray.plantingIds.length) {
            throw new Error("Cannot update planting: germination tray or slot is unavailable");
        }
        const updatedTray = {
            ...tray,
            plantingIds: [...tray.plantingIds],
        };
        updatedTray.plantingIds[index] = plantingId ?? null;
        await get().savePlantingIds(updatedTray);
    },
}));
